import React, { ReactElement, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { Button } from 'app/scripts/components/moderation-v2/sharable/components/Button/Button';
import { Spinner } from 'app/scripts/components/moderation-v2/restricted-words-next/components/Spinner/Spinner';
import { MultiSelectionInput } from 'app/scripts/components/moderation-v2/restricted-words-next/components/Inputs/MultiSelectionInput/MultiSelectionInput';
import {
  AddButton,
  RemoveButton
} from 'app/scripts/components/moderation-v2/sharable/components/IconButton';
import { RestrictedGroup } from 'app/scripts/components/moderation-v2/restricted-words-next/restrictedWordsTypes';
import {
  fetchRestrictedGroups,
  assignGroupToArticle
} from 'app/scripts/components/moderation-v2/restricted-words-next/restrictedActions';
import Icon from 'utils/components/icon';

import styles from './AssignGroupModal.module.scss';
import { useTypedSelector } from 'state/store-types';
import { useArticlePropsById } from 'app/scripts/components/moderation-v2/articles/hooks/useArticlePropsById';
import { mergeAndSortUniqueGroups } from 'app/scripts/components/moderation-v2/restricted-words-next/restrictedWordsUtils';

export interface AssignGroupModalProps {
  onClose?: () => void;
  description?: string;
  title?: string;
  close?: string;
  postId: string;
  articleId: string;
  articleName: string;
  restrictedWordsGroups: RestrictedGroup[];
}

export const AssignGroupModal = ({
  onClose = () => {},
  description,
  title,
  articleId,
  postId,
  articleName
}: AssignGroupModalProps): ReactElement => {
  const isLoading = useTypedSelector(state => state.moderation.restrictedWords.isLoading);
  const groups = useTypedSelector(state => state.moderation.restrictedWords.groups);
  const [selectedGroups, setSelectedGroups] = useState<Array<RestrictedGroup>>([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [suggestionIsOpen, setSuggestionsIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (groups && selectedGroups && !suggestionIsOpen) {
      const mergedGroups = mergeAndSortUniqueGroups(groups, selectedGroups);
      setSuggestions(mergedGroups);
    }
  }, [groups, selectedGroups, suggestionIsOpen]);

  const { restrictedWordsGroups } = useArticlePropsById(articleId) ?? {};

  const dispatch = useDispatch();

  const initialSelectedGroups = useRef(restrictedWordsGroups);

  useEffect(() => {
    dispatch(fetchRestrictedGroups());
  }, [dispatch]);

  useEffect(() => {
    if (restrictedWordsGroups) {
      setSelectedGroups(restrictedWordsGroups);
    }
  }, [restrictedWordsGroups]);

  const handleSave = () => {
    dispatch(
      assignGroupToArticle({
        postId,
        articleName,
        restrictedGroups: selectedGroups,
        undoRestrictedGroups: restrictedWordsGroups,
        successCallback: () => {
          onClose();
        }
      })
    );
  };

  const onAddClick = (item: RestrictedGroup) => {
    setSelectedGroups([...selectedGroups, item]);
    setSuggestions(
      suggestions.map(suggestion =>
        suggestion.id === item.id ? { ...suggestion, isAssigned: true } : suggestion
      )
    );
  };

  const onRemoveSelectedItems = (item: RestrictedGroup) => {
    if (selectedGroups.length > 1) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(selectedGroups.filter(selectedItem => selectedItem.id !== item.id));
    }
  };

  const renderSuggestionActionButtons = (item: RestrictedGroup) => {
    if (item.isAssigned) {
      return (
        <RemoveButton
          onClick={() => {
            setSelectedGroups(selectedGroups.filter(selectedItem => selectedItem.id !== item.id));
            setSuggestions(
              suggestions.map(suggestion =>
                suggestion.id === item.id ? { ...suggestion, isAssigned: false } : suggestion
              )
            );
          }}
        />
      );
    }
    return <AddButton onClick={() => onAddClick(item)} className={styles.addButton} />;
  };

  useEffect(() => {
    const isChanged = !compareArrays(selectedGroups, initialSelectedGroups.current);

    setIsDataChanged(isChanged);
  }, [selectedGroups]);

  return (
    <div className={styles.assignGroupModal}>
      {title && (
        <div className={styles.assignGroupHeader}>
          <Icon name="tags" className={styles.tagsIcon} />
          <span className={styles.title}>{title}</span>
        </div>
      )}
      <div className={styles.assignGroupDescription}>{description}</div>
      <MultiSelectionInput
        placeholder="Assign restriction groups"
        itemType="groups"
        suggestions={suggestions}
        selectedItems={selectedGroups}
        onRemoveSelectedItems={onRemoveSelectedItems}
        onSuggestionsVisibilityChange={visible => setSuggestionsIsOpen(visible)}
        renderSuggestion={item => (
          <div className={styles.suggestionContainer}>
            <div
              className={classNames(styles.suggestionValue, styles.groupItem, {
                [styles.assignedGroupItem]: item.isAssigned
              })}
            >
              <div className={styles.item}>{item.value}</div>
              <div className={styles.wordCount}>{item.wordsCount}</div>
            </div>
            {renderSuggestionActionButtons(item)}
          </div>
        )}
      />
      <div className={styles.buttonsContainer}>
        <Button theme="contained-discard" disabled={isLoading} onClick={onClose}>
          Close
        </Button>
        <Button theme="contained-apply" disabled={isLoading || !isDataChanged} onClick={handleSave}>
          {isLoading ? <Spinner /> : <>Save</>}
        </Button>
      </div>
    </div>
  );
};

function compareArrays(array1: Array<RestrictedGroup>, array2: Array<RestrictedGroup>) {
  if (array1.length !== array2.length) {
    return false;
  }

  const copyArray1 = [...array1];
  const copyArray2 = [...array2];

  copyArray1.sort((a, b) => a.id - b.id);
  copyArray2.sort((a, b) => a.id - b.id);

  for (let i = 0; i < copyArray1.length; i++) {
    if (copyArray1[i].id !== copyArray2[i].id) {
      return false;
    }
  }

  return true;
}
